import type { InputProps } from '@chakra-ui/input';
import { Input, InputGroup, InputLeftElement, InputRightElement } from '@chakra-ui/input';
import { Tooltip } from '@chakra-ui/react';
import { forwardRef } from 'react';

import { IoAlertCircleIcon } from '@/components/common/Icons/IoAlertCircleIcon';
import { WizardAskButton } from '@/components/wizard/WizardAskButton';

import ErrorMessage from '../ErrorMessage/ErrorMessage';
import Label from '../Label/Label';
import { inputDefaultStyles } from '../utils';

export interface TextInputProps extends InputProps {
  label?: string;
  secondaryLabel?: string;
  description?: React.ReactNode;
  errorMessage?: string;
  errorMessageType?: 'underMessage' | 'tooltip';
  required?: boolean;
  isDisabled?: boolean;
  tipMessage?: string;
  showTipMessage?: boolean;
  /**
   * @deprecated use isDisabled instead
   */
  disabled?: boolean;
  readOnly?: boolean;
  inputLeftElement?: React.ReactNode;
  inputRightElement?: React.ReactNode;
  labelFontSize?: number | string;
  testId?: string;
  enableWizardAsk?: boolean;
  wizardAskPlacement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  wrapperStyle?: React.CSSProperties;
  hasLabelTooltip?: boolean; // need to differentiate program management text input from others
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      secondaryLabel,
      errorMessage,
      errorMessageType = 'underMessage',
      tipMessage = '',
      required = false,
      isDisabled = false,
      disabled = false,
      readOnly = false,
      inputLeftElement = null,
      inputRightElement = null,
      size = 'sm',
      labelFontSize,
      testId,
      enableWizardAsk = false,
      wizardAskPlacement = 'bottom-start',
      wrapperStyle,
      showTipMessage,
      hasLabelTooltip = false,
      ...props
    },
    ref,
  ) => {
    const isNumberInput = props.type === 'number';
    const baseLabelComponent = (
      <Label
        name={props.name}
        label={label}
        secondaryLabel={secondaryLabel}
        labelFontSize={labelFontSize}
        showTooltipIcon={showTipMessage}
        tooltipLabel={tipMessage}
        required={required}
      />
    );

    const labelComponent =
      (typeof label === 'string' || typeof secondaryLabel === 'string') &&
      (enableWizardAsk ? (
        <WizardAskButton
          question={`${label || ''} ${props.value || ''}`}
          testid={testId}
          placement={wizardAskPlacement}
        >
          {baseLabelComponent}
        </WizardAskButton>
      ) : (
        baseLabelComponent
      ));

    return (
      <div style={wrapperStyle}>
        <div>
          {labelComponent}
          <InputGroup size={size} my={1}>
            {inputLeftElement && <InputLeftElement h='full'>{inputLeftElement}</InputLeftElement>}
            <Input
              id={props.name}
              isRequired={required}
              isDisabled={disabled || isDisabled}
              isReadOnly={readOnly}
              isInvalid={!!errorMessage}
              {...inputDefaultStyles}
              ref={ref}
              data-testid={testId || props.name}
              onWheel={isNumberInput ? (event) => event.currentTarget.blur() : undefined}
              onKeyDown={(event) => {
                if (isNumberInput && (event.key === 'e' || event.key === 'E')) {
                  event.preventDefault();
                }
              }}
              {...props}
            />
            {inputRightElement && (
              <InputRightElement w='auto' h='full' mr={3} color={'gray.400'} justifyContent={'flex-end'}>
                {inputRightElement}
              </InputRightElement>
            )}

            {!hasLabelTooltip && tipMessage && (
              <InputRightElement h='full'>
                <Tooltip label={tipMessage} hasArrow bgColor='gray.900'>
                  <IoAlertCircleIcon color='orange.500' />
                </Tooltip>
              </InputRightElement>
            )}

            {errorMessage && errorMessageType === 'tooltip' && (
              <InputRightElement h='full'>
                <Tooltip label={errorMessage} hasArrow bgColor='gray.900'>
                  <IoAlertCircleIcon color='red.500' />
                </Tooltip>
              </InputRightElement>
            )}
          </InputGroup>
        </div>
        {errorMessage && errorMessageType === 'underMessage' ? (
          <ErrorMessage message={errorMessage} data-testid={`error-message-${props.name}`} />
        ) : (
          props.description
        )}
      </div>
    );
  },
);

TextInput.displayName = 'TextInput';

export default TextInput;
